/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useCallback, useEffect, useState } from "react";

import { CosmosRichText, CosmosText, EdsIcon } from "@components/elements";
import { css, cva, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

import { Accordion } from "..";

import { getFaqId, getShortId } from "@components/utils";
import { FAQItem, FAQProps, hasFAQHash } from "@components/utils/faq.utils";

const faqStyles = cva({
	base: {
		width: "full",
	},
	variants: {
		titleVariant: {
			headingXL: {
				"&&": {
					gap: "eds.dimension.32",
				},
				"& .faq-heading": {
					textStyle: "headingXL",
				},
				"& .faq-sub-heading": {
					textStyle: "headingMD",
				},
				"& .faq-item-content": {
					textStyle: "paragraphMD",
				},
				smDown: {
					"& .faq-heading": {
						textStyle: "headingLG",
					},
					"& .faq-sub-heading": {
						textStyle: "headingSM",
					},
					"& .faq-item-content": {
						textStyle: "paragraphSM",
					},
				},
			},
			headingLG: {
				"&&": {
					gap: "eds.dimension.16",
				},
				"& .faq-heading": {
					textStyle: "headingLG",
				},
				"& .faq-sub-heading": {
					textStyle: "headingMD",
				},
				"& .faq-item-content": {
					textStyle: "paragraphSM",
				},
				smDown: {
					"& .faq-heading": {
						textStyle: "headingMD",
					},
					"& .faq-sub-heading": {
						textStyle: "headingSM",
					},
					"& .faq-item-content": {
						textStyle: "paragraphXS",
					},
				},
			},
		},
	},
});

const itemStyles = css({
	border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	backgroundColor: "eds.material.elevated.high.weak",
	outlineOffset: "eds.dimension.2",
	width: "100%",
	"&:has(:focus-visible)": {
		outline: "token(spacing.eds.dimension.2) solid #fff",
	},
	borderRadius: "eds.borderRadius.24",
	padding: "eds.dimension.24",
	smDown: {
		borderRadius: "eds.borderRadius.16",
		padding: "eds.dimension.16",
	},
});

const triggerStyles = css({
	cursor: "pointer",
	outline: "none",
	textAlign: "start",
	margin: "-eds.dimension.24",
	padding: "eds.dimension.24",
	width: "calc(100% + (2 * token(spacing.eds.dimension.24)))",
	"&:has(:focus-visible), &:is(:focus-visible,:hover)": {
		"& .faq-icon-wrapper": {
			backgroundColor: "eds.fill.secondary.hover",
		},
	},
	smDown: {
		margin: "-eds.dimension.16",
		padding: "eds.dimension.16",
		width: "calc(100% + (2 * token(spacing.eds.dimension.16)))",
	},
});

const triggerHeadingStyle = css({
	smDown: {
		overflow: "hidden",
		overflowWrap: "break-word",
	},
});

const iconWrapperStyles = css({
	display: "flex",
	width: "eds.dimension.32",
	height: "eds.dimension.32",
	padding: "eds.dimension.6",
	flexShrink: "0",
	justifyContent: "center",
	alignItems: "center",
	borderRadius: "eds.borderRadius.full",
	backgroundColor: "eds.fill.secondary.default",
	transition: "background-color token(animations.eds.duration.100) token(animations.eds.easing.easeInOutQuad)",
});

const iconStyles = cva({
	base: {
		flexShrink: 0,
		transition: "transform token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)",
	},
	variants: {
		expanded: {
			true: { transform: "rotate(180deg)" },
		},
	},
});

const contentContainerStyles = css({
	overflow: "hidden",

	"&[data-state='open']": {
		animation: `radixAccordionSlideDown token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)`,
	},

	"&[data-state='closed']": {
		animation: `radixAccordionSlideUp token(animations.eds.duration.300) token(animations.eds.easing.easeInOutQuad)`,
	},
});

const contentStyles = css({
	paddingBlockStart: "eds.dimension.16",
});

export const FAQ = ({ id, items = [], title, titleVariant = "headingXL" }: FAQProps): React.JSX.Element => {
	const [value, setValue] = useState<string[]>([]);
	const [currentId, setCurrentId] = useState<string | undefined>();

	const itemId = useCallback((item: FAQItem, i: number): string => getFaqId(getShortId(item?.id || `${i}`)), []);
	const isExpanded = useCallback((id: string): boolean => value && value.includes(id), [value]);

	useEffect(() => {
		if (typeof currentId === "string" && id === currentId) return; // we reset only on id change or initial
		const defaultValue: string[] = [];
		items?.forEach((item, i) => {
			if (item?.defaultOpen) {
				defaultValue.push(itemId(item, i));
			}
		});
		setValue(defaultValue);
		setCurrentId(id || "");
	}, [currentId, id, itemId, items]);

	useEffect(() => {
		const locationHash = window.location.hash; // fix for FAQ-Search, to not re-run between changing tabs as tab will change hash on client side
		if (!locationHash?.startsWith("#") || locationHash.length < 2) return;

		const hash = locationHash.slice(1);
		const currentIndex = items.findIndex((item) => hasFAQHash(item, hash));

		if (currentIndex < 0) return;

		const newEl = items[currentIndex];
		const el = document.getElementById(newEl?.anchorLinkId || newEl?.fallbackLinkId);
		if (!el) return;

		setValue((oldValue) => [...oldValue, itemId(newEl, currentIndex)]);
		setTimeout(() => {
			el?.scrollIntoView();
		}, 250);
	}, [items, itemId]);

	if (!items || !items?.length) return <></>;

	return (
		<VStack alignItems="stretch" className={faqStyles({ titleVariant })}>
			{title ? (
				<CosmosText color="eds.text.primary" className="faq-heading" as="h2">
					{title}
				</CosmosText>
			) : null}

			<Accordion value={value} onValueChange={setValue}>
				<VStack alignItems="start" gap="eds.dimension.16">
					{items?.map((item, i) => {
						if (!item.question?.html) return null;
						return (
							<Accordion.Item
								className={itemStyles}
								key={itemId(item, i)}
								value={itemId(item, i)}
								id={item?.anchorLinkId || item?.fallbackLinkId}
							>
								<Accordion.Trigger className={cx("faq-trigger", triggerStyles)}>
									<HStack justifyContent="space-between">
										<CosmosRichText
											color="eds.text.primary"
											html={item.question?.html}
											className={cx("faq-sub-heading", triggerHeadingStyle)}
											as="h3"
										/>
										<div className={cx("faq-icon-wrapper", iconWrapperStyles)}>
											<EdsIcon
												icon="ChevronDown"
												className={iconStyles({ expanded: isExpanded(`${itemId(item, i)}`) })}
												color="primary"
												size="xs"
											/>
										</div>
									</HStack>
								</Accordion.Trigger>
								<Accordion.Content className={contentContainerStyles}>
									<div className={contentStyles}>
										<CosmosRichText color="eds.text.secondary" html={item.answer?.html} className="faq-item-content" />
									</div>
								</Accordion.Content>
							</Accordion.Item>
						);
					})}
				</VStack>
			</Accordion>
		</VStack>
	);
};
